import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useState, useEffect } from 'react'
import useApi from '../api';
import { useParams } from 'react-router-dom';
import { formatDate, addMinutes } from '../utils/dateFormatter';
import Chat from './Chat';

interface Lead {
    id: string;
    first_name: string;
    last_name: string;
    company: string;
    email: string;
    job_title: string;
    attributes: any;
}

interface Message {
    id: string;
    role: string;
    content: string;
    created_at: string;
}

interface Conversation {
    id: string;
    created_at: string;
    last_activity: string;
    processed: boolean;
    messages: Message[];
    interaction: boolean;
    visitor_id?: string;
    city: string;
    region: string;
    country: string;
    postal: string;
    lead?: Lead;
}

export default function Session() {
    const { id } = useParams();
    const [modalVisible, setModalVisible] = useState(false);
    let [isOpen, setIsOpen] = useState(false);
    const { get } = useApi();
    const [session, setSession] = useState<Conversation | null>(null);
    const [message, setMessage] = useState<Message | null>(null);

    function closeModal() {
        setModalVisible(false);
        setIsOpen(false);
    }

    function openModal() {
        setIsOpen(true);
    }

    useEffect(() => {
        if (id) {
            get(`/sessions/${id}`).then((response) => {
                setSession(response.data);
            }).catch((error) => {
                console.error('Error fetching session:', error);
            });
        }
    }, [id]);

    useEffect(() => {
        if (message) {
            openModal();
        }
    }, [message]);

    function isLink(str: string): boolean {
        try {
            new URL(str);
            return true;
        } catch {
            return false;
        }
    }

    return (
        <>
            {session && (
                <div className="flex w-full h-full">
                    <div className="w-1/4 transform h-full bg-white p-6 text-left align-middle overflow-y-auto scrollbar-thin dark:bg-slate-900">
                        <div className="text-xl font-medium leading-6 text-slate-600 mb-6 px-4 py-2 text-center border bg-slate-100 rounded-md dark:bg-slate-800 dark:text-slate-100 dark:border-none">
                            Session Details
                        </div>
                            <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100">
                                User ID
                            </label>
                            <div className="text-sm mt-1 dark:text-slate-200">
                                <h1>
                                    {session.visitor_id}
                                </h1>
                            </div>
                            <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100">
                                Session Started
                            </label>
                            <div className="text-sm mt-1 mb-6 dark:text-slate-200">
                                <h1>
                                    {formatDate(session.created_at)}
                                </h1>
                            </div>
                            <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100">
                                Session Last Active
                            </label>
                            <div className="text-sm mt-1 mb-6 dark:text-slate-200">
                                <h1>
                                    {session.last_activity ? formatDate(session.last_activity) : addMinutes(session.created_at, 1)}
                                </h1>
                            </div>
                            <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100 s">
                                Session Processed
                            </label>
                            <div className="text-sm mt-1 mb-6 dark:text-slate-200">
                                <h1>
                                    {session.processed ? "Yes" : "No"}
                                </h1>
                            </div>
                            <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100 s">
                                City
                            </label>
                            <div className="text-sm mt-1 mb-6 dark:text-slate-200">
                                <h1>
                                    {session.city}
                                </h1>
                            </div>
                            <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100 s">
                                Region
                            </label>
                            <div className="text-sm mt-1 mb-6 dark:text-slate-200">
                                <h1>
                                    {session.region}
                                </h1>
                            </div>
                            <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100 s">
                                Country
                            </label>
                            <div className="text-sm mt-1 mb-6 dark:text-slate-200">
                                <h1>
                                    {session.country}
                                </h1>
                            </div>
                            <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100 s">
                                Zip Code
                            </label>
                            <div className="text-sm mt-1 mb-6 dark:text-slate-200">
                                <h1>
                                    {session.postal}
                                </h1>
                            </div>
                        {session.lead && (
                            <>
                                <div className="text-xl font-medium leading-6 text-slate-600 mb-6 px-4 py-2 text-center border bg-slate-100 rounded-md dark:bg-slate-800 dark:text-slate-100 dark:border-none">
                                    Lead Details
                                </div>
                                <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100">
                                    Name
                                </label>
                                <div className="text-sm mt-1 dark:text-slate-200">
                                    <h1>
                                        {session.lead.first_name} {session.lead.last_name}
                                    </h1>
                                </div>
                                <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100">
                                    Email
                                </label>
                                <div className="text-sm mt-1 dark:text-slate-200">
                                    <h2>
                                        {session.lead.email}
                                    </h2>
                                </div>
                                <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100">
                                    Company
                                </label>
                                <div className="text-sm mt-1 dark:text-slate-200">
                                    <h3>
                                        {session.lead.company}
                                    </h3>
                                </div>
                                <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-100">
                                    Title
                                </label>
                                <div className="text-sm mt-1 dark:text-slate-200">
                                    <h4>
                                        {session.lead.job_title}
                                    </h4>
                                </div>
                                {session.lead.attributes && Object.keys(session.lead.attributes).map((key) => {
                                    if (key === "email" || key === "first_name" || key === "last_name" || key === "company" || key === "job_title") {
                                        return null;
                                    } else if (session.lead) {
                                        return (
                                            <div key={key}>
                                                <label className="block text-md font-medium leading-6 text-slate-900 pb-1 mt-6 dark:text-slate-200">
                                                    {key}
                                                </label>
                                                <div className="">
                                                    {isLink(session.lead.attributes[key]) ? (
                                                        <a href={session.lead.attributes[key]} className="text-blue-500 underline break-words text-sm font-medium" target="_blank" rel="noopener noreferrer">
                                                            {session.lead.attributes[key]}
                                                        </a>
                                                    ) : (
                                                        <div className="text-sm mt-1">
                                                            {session.lead.attributes[key]}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    }
                                })}
                            </>
                        )}

                    </div>
                    <Chat chatId={session.id} />
                </div>
            )}
        </>
    )
}
